import {
  PublicRouteId,
  PrivateRouteId,
  ROOT_ROUTE_PATH,
  StandaloneRouteId,
  FALLBACK_ROUTE_PATH,
  PUBLIC_ROUTE_REGISTRY,
  PRIVATE_ROUTE_REGISTRY,
  STANDALONE_ROUTE_REGISTRY,
} from 'constants/routes';
import * as Pages from 'pages';
import { Authentication } from 'utils';
import { useLanguageContext } from 'providers/language';
import { useSnowSettings } from 'providers/snow-settings';
import { ComponentType, useEffect, useState } from 'react';
import { Layout, NewYearSnowOverlay, RouteGuard } from 'components';
import { isNewYearSnowDayTR } from 'components/new-year-snow-overlay';
import { Routes, Navigate, Route, BrowserRouter } from 'react-router-dom';

const privateRouteComponents = {
  projectStepManaging: Pages.ProjectStepManaging,
  projectPlanGenerate: Pages.ProjectPlanGenerate,
  customerSourceTypes: Pages.CustomerSourceTypes,
  effortStatusContacts: Pages.EffortStatusContacts,
  freshdeskFeedbackReport: Pages.FreshdeskFeedbackReport,
  users: Pages.Users,
  roles: Pages.Roles,
  report: Pages.Report,
  book: Pages.CreateBook,
  profile: Pages.Profile,
  projects: Pages.Projects,
  timeline: Pages.Timeline,
  gibTypes: Pages.GibTypes,
  dashboard: Pages.Dashboard,
  usedBanks: Pages.UsedBanks,
  generalReport: Pages.GeneralReport,
  activityTypes: Pages.ActivityTypes,
  effortTracing: Pages.EffortTracing,
  productReport: Pages.ProductReport,
  projectGroups: Pages.ProjectGroups,
  effortTimeline: Pages.EffortTimeline,
  effortContacts: Pages.EffortContacts,
  connectionTypes: Pages.ConnectionTypes,
  salesStageTypes: Pages.SalesStageTypes,
  personnelHoliday: Pages.PersonnelHoliday,
  environmentTypes: Pages.EnvironmentTypes,
  activityManaging: Pages.ActivityManaging,
  holidayMonitoring: Pages.HolidayMonitoring,
  projectMonitoring: Pages.ProjectMonitoring,
  holidayYearlyView: Pages.HolidayYearlyView,
  freshdeskDashboard: Pages.FreshdeskDashboard,
  activityMonitoring: Pages.ActivityMonitoring,
  productProjectPlan: Pages.ProductProjectPlan,
} satisfies Record<PrivateRouteId, ComponentType>;

const publicRouteComponents = {
  login: Pages.Login,
  signUp: Pages.SignUp,
  forgotPassword: Pages.ForgotPassword,
} satisfies Record<PublicRouteId, ComponentType>;

const standaloneRouteComponents = {
  confirmEffort: Pages.ConfirmEffort,
  ticketSurvey: Pages.TicketSurvey,
} satisfies Record<StandaloneRouteId, ComponentType>;

const App = () => {
  // useState hooks
  const { enabled, color } = useSnowSettings();
  const [showSnow, setShowSnow] = useState(() => isNewYearSnowDayTR());

  // dynamic variables
  const { language } = useLanguageContext();
  const hasSession = Authentication.hasSession();
  const redirect = hasSession ? PRIVATE_ROUTE_REGISTRY.dashboard.path : PUBLIC_ROUTE_REGISTRY.login.path;
  const ticketSurveyBasePath = STANDALONE_ROUTE_REGISTRY.ticketSurvey.basePath;
  const isTicketSurveyRoute =
    window.location.pathname === ticketSurveyBasePath ||
    window.location.pathname.startsWith(`${ticketSurveyBasePath}/`);

  // useEffect hooks
  useEffect(() => {
    document.documentElement.lang = language;
    document.title = isTicketSurveyRoute
      ? `Idecon | ${language === 'tr' ? 'Destek Değerlendirmesi' : 'Support Feedback'}`
      : `Idecon | ${language === 'tr' ? 'Proje Takip Sistemi' : 'Project Tracking System'}`;
  }, [isTicketSurveyRoute, language]);

  useEffect(() => {
    const id = window.setInterval(() => setShowSnow(isNewYearSnowDayTR()), 60_000);

    return () => window.clearInterval(id);
  }, []);

  return (
    <BrowserRouter>
      <Layout>
        <Routes>
          {/* Standalone Public Routes */}
          {(Object.keys(STANDALONE_ROUTE_REGISTRY) as StandaloneRouteId[]).flatMap((routeId) => {
            const Component = standaloneRouteComponents[routeId];

            return STANDALONE_ROUTE_REGISTRY[routeId].paths.map((path) => (
              <Route path={path} element={<Component />} key={path} />
            ));
          })}

          {/* Public Routes */}
          {(Object.keys(PUBLIC_ROUTE_REGISTRY) as PublicRouteId[]).map((routeId) => {
            const Component = publicRouteComponents[routeId];
            const { path } = PUBLIC_ROUTE_REGISTRY[routeId];

            return (
              <Route
                path={path}
                key={routeId}
                element={
                  <RouteGuard access="guest">
                    <Component />
                  </RouteGuard>
                }
              />
            );
          })}

          {/* Private Routes */}
          {(Object.keys(PRIVATE_ROUTE_REGISTRY) as PrivateRouteId[]).map((routeId) => {
            const Component = privateRouteComponents[routeId];
            const { path, anyOfPermissions } = PRIVATE_ROUTE_REGISTRY[routeId];

            return (
              <Route
                path={path}
                key={routeId}
                element={
                  <RouteGuard access="private" anyOfPermissions={anyOfPermissions}>
                    <Component />
                  </RouteGuard>
                }
              />
            );
          })}

          <Route path={ROOT_ROUTE_PATH} element={<Navigate to={redirect} />} />
          <Route path={FALLBACK_ROUTE_PATH} element={<Navigate to={redirect} />} />
        </Routes>
      </Layout>
      {!isTicketSurveyRoute && showSnow && enabled && <NewYearSnowOverlay color={color} />}
    </BrowserRouter>
  );
};

export default App;
